import { useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Table,
  Badge,
} from "reactstrap";
import { Btn } from "../../../AbstractElements";
import { useNavigate } from "react-router-dom";
import { OrderActionsCell } from "./OrderActionsCell";
import {
  DELIVERY_TYPE_LABELS,
  getStatusColor,
  ORDER_STATUS_LABELS,
  PAYMENT_METHOD_LABELS,
  PAYMENT_PROVIDER_LABELS,
} from "../../utils/orderConstants";
import { PaymentProofModal } from "./PaymentProofModal";
import { formatDate } from "../../utils/formatDate";
import { PAYMENT_CHANNEL_CONFIG } from "./order.columns";

/** @param actions Botones extra del detalle (estado Inter, despachar); van al final, a la derecha. */
export const CardSummaryOrder = ({ order, actions }) => {
  const navigate = useNavigate();
  const [proofModalOpen, setProofModalOpen] = useState(false);

  if (!order) return null;

  const user = order.user_id || {};
  const productCount = order.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
  const subtotal = order.subtotal || 0;
  const commission = order.comission?.value || 0;
  const percentage = order.comission?.percentage || 0;
  const total = order.total || 0;

  const isStorePickupTransfer =
    order.delivery_type === "STORE_PICKUP" && order.payment_method === "TRANSFER";

  const openPaymentProof = () => setProofModalOpen(true);
  const closePaymentProof = () => setProofModalOpen(false);

  return (
    <>
      <Card className="shadow-sm">
        <CardHeader tag="h5" className="d-flex justify-content-between align-items-center">
          Resumen de la orden

          <div className="d-flex gap-2 flex-wrap justify-content-end align-items-center">
            <Btn
              attrBtn={{
                color: "light",
                onClick: () => navigate(-1),
                size: "sm",
              }}
            >
              <i className="fa fa-arrow-left me-1" />
              Volver
            </Btn>
            <OrderActionsCell order={order} showDetail={false} />
            {/* Acción principal (despachar) al final, a la derecha */}
            {actions}
          </div>
        </CardHeader>

        <CardBody>
          <Table borderless responsive className="mb-0">
            <tbody>
              <tr>
                <th>ID orden</th>
                <td>
                  {order.id}
                </td>
              </tr>
              <tr>
                <th>Cliente</th>
                <td>
                  {user.name} <br />
                  <small>{user.email}</small>
                </td>
              </tr>

              <tr>
                <th>Estado</th>
                <td>
                  <Badge color={getStatusColor(order.status)}>
                    {ORDER_STATUS_LABELS[order.status] || order.status}
                  </Badge>
                </td>
              </tr>

              <tr>
                <th>Fecha de creación</th>
                <td>{formatDate(order.created_at)}</td>
              </tr>


              <tr>
                <th>Pagado</th>
                <td>
                  {order.status === "CANCELLED" ? (
                    <Badge color="danger">Cancelada</Badge>
                  ) : (
                    <Badge color={order.is_paid ? "success" : "warning"}>
                      {order.is_paid ? "Sí" : "No"}
                    </Badge>
                  )}
                </td>
              </tr>

              {order.payment_paid_approval && (
                <tr>
                  <th>Fecha de pago</th>
                  <td>{formatDate(order.payment_paid_approval)}</td>
                </tr>
              )}

              <tr>
                <th>Estado de la transacción</th>
                <td>
                  {order.payment_response
                    ? order.payment_response?.data?.transaction?.status === "APPROVED"
                      ? "Aprobado"
                      : "Rechazado"
                    : "Pendiente"}
                </td>
              </tr>

              <tr>
                <th>Tipo de entrega</th>
                <td>{DELIVERY_TYPE_LABELS[order.delivery_type] || order.delivery_type}</td>
              </tr>

              {(order.interrapidisimo_guide || order.servientrega_guide) && (
                <tr>
                  <th>Guía de envío</th>
                  <td>
                    {order.interrapidisimo_guide && (
                      <>
                        <i className="fa fa-truck me-2 text-primary" />
                        <strong>Interrapidisimo:</strong>{" "}
                        <span className="font-monospace">{order.interrapidisimo_guide}</span>
                      </>
                    )}
                    {order.interrapidisimo_guide && order.servientrega_guide && <br />}
                    {order.servientrega_guide && (
                      <>
                        <i className="fa fa-truck me-2 text-info" />
                        <strong>Servientrega:</strong>{" "}
                        <span className="font-monospace">{order.servientrega_guide}</span>
                      </>
                    )}
                  </td>
                </tr>
              )}

              <tr>
                <th>Productos</th>
                <td>{productCount}</td>
              </tr>

              {order.delivery_type === "DELIVERY" && order.shipping_cost ? (
                <tr>
                  <th>Costo de envío</th>
                  <td>${order.shipping_cost.toLocaleString()}</td>
                </tr>
              ) : null}

              <tr>
                <th>Subtotal</th>
                <td>${subtotal.toLocaleString()}</td>
              </tr>

              <tr>
                <th>Servicio On Line ({percentage.toLocaleString()}%) </th>
                <td>${commission.toLocaleString()}</td>
              </tr>

              {order.payment_channel === "card_payment" && (
                <tr>
                  <th>Servicio On Line</th>
                  <td>$500</td>
                </tr>
              )}

              {order.reshipments?.map((reshipment, index) => (
                <tr key={reshipment._id || index}>
                  <th className="text-capitalize">{reshipment.concept}</th>
                  <td>
                    ${reshipment.value.toLocaleString()}{" "}
                    <small className="text-muted">
                      ({reshipment.carrier_id}, guía {reshipment.guide}
                      {reshipment.previous_guide && `, reemplaza ${reshipment.previous_guide}`})
                    </small>
                  </td>
                </tr>
              ))}

              <tr>
                <th>Total</th>
                <td>${total.toLocaleString()}</td>
              </tr>

              <tr>
                <th>Método de pago</th>
                <td>
                  {PAYMENT_METHOD_LABELS[order.payment_method] || order.payment_method}

                  {order.payment_provider && (
                    <>
                      {" "}
                      (
                      {PAYMENT_PROVIDER_LABELS[order.payment_provider] || order.payment_provider}
                      )
                    </>
                  )}

                  {order.payment_provider === "MONNET" && order.payment_channel && (
                    <>
                      {" "}
                      - {PAYMENT_CHANNEL_CONFIG[order.payment_channel]?.label || order.payment_channel}
                    </>
                  )}

                  {isStorePickupTransfer && (
                    <button
                      type="button"
                      onClick={openPaymentProof}
                      className="ms-2 text-decoration-underline btn btn-link p-0 border-0"
                      style={{ verticalAlign: "baseline" }}
                    >
                      Ver comprobante
                    </button>
                  )}
                </td>
              </tr>


              {order.payment_provider && (
                <tr>
                  <th>Proveedor</th>
                  <td>{PAYMENT_PROVIDER_LABELS[order.payment_provider]}</td>
                </tr>
              )}

              {order.payment_response?.data?.transaction?.id && (
                <tr>
                  <th>Referencia Wompi</th>
                  <td>{order.payment_response?.data?.transaction?.id}</td>
                </tr>
              )}


              {order.delivered_at && (
                <tr>
                  <th>Fecha de entrega</th>
                  <td>
                    <i className="fa fa-calendar-check me-2 text-success" />
                    {formatDate(order.delivered_at)}
                  </td>
                </tr>
              )}

              {order.cancel_reason && (
                <>
                  <tr>
                    <th>Motivo de cancelación</th>
                    <td className="text-danger">
                      <i className="fa fa-exclamation-circle me-2 text-danger" />
                      {order.cancel_reason}
                    </td>
                  </tr>
                  {order.cancelled_at && (
                    <tr>
                      <th>Fecha de cancelación</th>
                      <td>
                        <i className="fa fa-calendar me-2 text-muted" />
                        {formatDate(order.cancelled_at)}
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </Table>
        </CardBody>
      </Card>

      <PaymentProofModal
        isOpen={proofModalOpen}
        toggle={closePaymentProof}
        order={order}
      />
    </>
  );
};
